import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductCard } from "@/components/product-card";
import { SEO } from "@/components/seo";
import type { Category, Product } from "@shared/schema";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: category, isLoading: categoryLoading } = useQuery<Category>({
    queryKey: ["/api/categories", slug],
  });

  const { data: products, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/categories", slug, "products"],
  });

  if (categoryLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Skeleton className="h-8 w-64 mb-2" />
        <Skeleton className="h-5 w-96 mb-8" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <Skeleton className="w-full aspect-video rounded-t-md" />
              <CardContent className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-3" />
                <Skeleton className="h-9 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Category Not Found</h1>
        <p className="text-muted-foreground mb-4">
          The category you're looking for doesn't exist.
        </p>
        <Link href="/">
          <span className="text-primary cursor-pointer hover:underline">
            Back to Home
          </span>
        </Link>
      </div>
    );
  }

  const categoryJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sierrachartpro.com/" },
        { "@type": "ListItem", position: 2, name: category.group === "templates" ? "Templates" : "Indicators", item: `https://sierrachartpro.com/${category.group}` },
        { "@type": "ListItem", position: 3, name: category.name, item: `https://sierrachartpro.com/category/${category.slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name} - Sierra Chart ${category.group === "templates" ? "Templates" : "Indicators"}`,
      description: category.description || `Browse ${category.name} for Sierra Chart trading platform.`,
      url: `https://sierrachartpro.com/category/${category.slug}`,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={`${category.name} - Sierra Chart ${category.group === "templates" ? "Templates" : "Indicators"}`}
        description={category.description || `Buy ${category.name} for Sierra Chart. Professional ${category.group} for traders. Instant digital download.`}
        keywords={`${category.name}, ${category.name} Sierra Chart, ${category.name} trading, buy ${category.name}, download ${category.name}`}
        ogImage={category.image || undefined}
        jsonLd={categoryJsonLd}
      />
      <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6 flex-wrap" data-testid="breadcrumb">
        <Link href="/">
          <span className="cursor-pointer hover:text-foreground transition-colors">Home</span>
        </Link>
        <ChevronRight className="w-3 h-3" />
        <Link href={category.group === "templates" ? "/templates" : "/indicators"}>
          <span className="cursor-pointer hover:text-foreground transition-colors capitalize">
            {category.group}
          </span>
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground">{category.name}</span>
      </nav>

      {category.image && (
        <div className="relative rounded-md overflow-hidden mb-8">
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-48 sm:h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="text-category-title">
              {category.name}
            </h1>
            {category.description && (
              <p className="text-gray-300 mt-1">{category.description}</p>
            )}
          </div>
        </div>
      )}

      {!category.image && (
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold" data-testid="text-category-title">
            {category.name}
          </h1>
          {category.description && (
            <p className="text-muted-foreground mt-1">{category.description}</p>
          )}
        </div>
      )}

      {productsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <Skeleton className="w-full aspect-video rounded-t-md" />
              <CardContent className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-3" />
                <Skeleton className="h-9 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : products && products.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">
              No products available in this category yet. Check back soon!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
